import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, Btn, SeverityBadge } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { PatchManager } from "@/components/PatchManager";
import { timeAgo, fmtUptime } from "@/lib/ui";
import {
  Cpu, MemoryStick, HardDrive, Network, Clock, ShieldCheck, Terminal, RefreshCw,
  Power, ScrollText, Play, Server, Activity, ExternalLink, Star, Eye, EyeOff, Copy, X,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { onLive } from "@/lib/live";
import { apiErr as _apiErr } from "@/lib/api";

const TABS = ["Overview", "Hardware", "Software", "Patches", "Metrics", "Processes", "Services", "Scripts/Jobs", "Security", "Audit History"];

export default function DeviceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [extra, setExtra] = useState({});
  const [termOpen, setTermOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [remotePick, setRemotePick] = useState(false);
  const [people, setPeople] = useState([]);
  const [rebootOpen, setRebootOpen] = useState(false);
  const [rebootWhen, setRebootWhen] = useState("");
  const [rebootReason, setRebootReason] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [actionJob, setActionJob] = useState(null);

  const trackJob = (jobId, label) => {
    if (!jobId) return;
    setActionJob({ id: jobId, label, status: "QUEUED" });
    let tries = 0;
    const poll = setInterval(async () => {
      tries++;
      try {
        const r = await api.get("/jobs/" + jobId);
        const tg = r.data.targets?.[0];
        const st = tg?.status || r.data.status || "QUEUED";
        setActionJob({ id: jobId, label, status: st });
        if (tg && ["SUCCEEDED", "FAILED", "TIMED_OUT"].includes(tg.status)) { clearInterval(poll); return; }
      } catch (e) { console.debug("Job poll failed, will retry:", e); }
      if (tries > 15) { clearInterval(poll); setActionJob((a) => a && a.id === jobId ? { ...a, status: "AWAITING AGENT" } : a); }
    }, 2000);
  };

  const load = useCallback(() => api.get("/devices/" + id).then((r) => setD(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); const t = setInterval(load, 20000); const off = onLive((e) => { if (e.device_id === id || e.type === "device") load(); }); return () => { clearInterval(t); off(); }; }, [load, id]);
  useEffect(() => { api.get("/remote/providers").then((r) => setProviders(r.data.filter((p) => p.enabled))).catch(() => {}); }, []);
  useEffect(() => { if (d?.customer_id) api.get("/people", { params: { customer_id: d.customer_id } }).then((r) => setPeople(r.data)).catch(() => {}); }, [d?.customer_id]);
  const assignPerson = async (cid) => { try { await api.post(`/devices/${id}/assign-person`, { contact_id: cid || null }); load(); toast.success("Owner updated"); } catch (e) { toast.error(_apiErr(e)); } };
  const uninstallApp = async (name) => { if (!window.confirm(`Uninstall "${name}" from ${d.hostname}? This queues an uninstall job on the endpoint.`)) return; try { const { data } = await api.post(`/devices/${id}/uninstall-software`, { name }); toast.success(`Uninstall queued for ${name}`); trackJob(data.id, `Uninstall ${name}`); } catch (e) { toast.error(_apiErr(e)); } };
  const endTask = async (pid, name) => { if (!window.confirm(`End task "${name}" (PID ${pid}) on ${d.hostname}?`)) return; try { const { data } = await api.post(`/devices/${id}/kill-process`, { pid }); toast.success(`End task queued for PID ${pid}`); trackJob(data.id, `End task ${name} (PID ${pid})`); } catch (e) { toast.error(_apiErr(e)); } };

  const startRemote = async (pid) => {
    try { const { data } = await api.post(`/devices/${id}/remote-session`, { provider_id: pid }); window.open(data.url, "_blank", "noopener"); toast.success("Launching " + data.provider); setRemotePick(false); }
    catch (e) { toast.error(_apiErr(e)); }
  };
  const remoteClick = () => { if (providers.length === 1) startRemote(providers[0].id); else setRemotePick(true); };
  const toggleCritical = async () => {
    try { const { data } = await api.patch(`/devices/${id}`, { is_critical: !d.is_critical }); setD(data); toast.success(data.is_critical ? "Marked critical" : "Unmarked critical"); }
    catch (e) { toast.error(_apiErr(e)); }
  };

  useEffect(() => {
    const map = {
      Metrics: () => api.get(`/devices/${id}/metrics`).then((r) => setExtra((e) => ({ ...e, metrics: r.data }))),
      Software: () => api.get(`/devices/${id}/software`).then((r) => setExtra((e) => ({ ...e, software: r.data }))),
      Processes: () => api.get(`/devices/${id}/processes`).then((r) => setExtra((e) => ({ ...e, processes: r.data }))),
      Services: () => api.get(`/devices/${id}/services`).then((r) => setExtra((e) => ({ ...e, services: r.data }))),
      "Scripts/Jobs": () => api.get(`/jobs?device_id=${id}`).then((r) => setExtra((e) => ({ ...e, jobs: r.data }))),
      "Audit History": () => api.get(`/devices/${id}/audit`).then((r) => setExtra((e) => ({ ...e, audit: r.data }))),
    };
    map[tab]?.();
  }, [tab, id]);

  const power = async (action) => {
    if ((action === "restart" || action === "shutdown") && !window.confirm(`${action} ${d.hostname}? Warn about unsaved work.`)) return;
    try { await api.post(`/devices/${id}/power`, { action }); toast.success(`${action} queued`); }
    catch (e) { toast.error(_apiErr(e)); }
  };
  const toggleMaint = async () => {
    try { const { data } = await api.post(`/devices/${id}/maintenance`, { enabled: d.status !== "MAINTENANCE" }); setD(data); toast.success("Maintenance " + (data.status === "MAINTENANCE" ? "enabled" : "disabled")); }
    catch (e) { toast.error(_apiErr(e)); }
  };
  const rebootNow = async () => {
    if (!window.confirm(`Reboot ${d.hostname} now?`)) return;
    try { await api.post(`/devices/${id}/reboot`, {}); toast.success("Reboot command queued"); }
    catch (e) { toast.error(_apiErr(e)); }
  };
  const scheduleReboot = async () => {
    try { await api.post(`/devices/${id}/reboot`, { when: new Date(rebootWhen).toISOString(), reason: rebootReason }); toast.success("Reboot scheduled"); setRebootOpen(false); setRebootWhen(""); setRebootReason(""); }
    catch (e) { toast.error(_apiErr(e)); }
  };
  if (!d) return <div className="p-6"><Spinner /></div>;
  const hw = d.hardware_inventory || {};
  const sec = d.security || {};

  return (
    <div className="p-6 fade-up pb-10">
      <button onClick={() => nav("/devices")} className="text-[12px] text-slate-500 hover:text-cyan-400 mb-2">← Devices</button>
      {/* Header */}
      <div className="card-surface rounded-lg p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-cyan-500/10 flex items-center justify-center">
            {d.device_type === "server" ? <Server size={20} className="text-cyan-400" /> : <Cpu size={20} className="text-cyan-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-heading font-extrabold text-xl text-slate-100 font-mono">{d.hostname}</h1>
              <StatusBadge status={d.status} />
              {d.is_critical && <span data-testid="critical-badge" className="text-[10px] font-bold uppercase bg-amber-950/60 text-amber-300 border border-amber-800/70 rounded px-1.5 py-0.5">Critical</span>}
            </div>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {d.customer_name}{d.site_name ? ` · ${d.site_name}` : ""} · {d.os} · agent {d.agent_version} · seen {timeAgo(d.last_seen)}
              {d.logged_in_user ? ` · ${d.logged_in_user}` : ""}
            </div>
            {(d.tags || []).length > 0 && <div className="flex gap-1 mt-1.5">{d.tags.map((t) => <span key={t} className="text-[10px] bg-slate-800 text-slate-300 rounded px-1.5 py-0.5">{t}</span>)}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            {can("remote:start") && providers.length > 0 && <Btn variant="ghost" onClick={remoteClick} data-testid="remote-btn"><ExternalLink size={14} /> Remote</Btn>}
            {can("commands:powershell") && <Btn variant="ghost" onClick={() => setTermOpen(true)} data-testid="open-terminal-btn"><Terminal size={14} /> Terminal</Btn>}
            {can("power:restart") && <Btn variant="ghost" onClick={() => power("restart")} data-testid="restart-btn"><RefreshCw size={14} /> Restart</Btn>}
            {can("power:shutdown") && <Btn variant="ghost" onClick={() => power("shutdown")}><Power size={14} /> Shutdown</Btn>}
            {can("devices:modify") && <Btn variant="ghost" onClick={toggleCritical} data-testid="critical-btn"><Star size={14} className={d.is_critical ? "text-amber-400" : ""} /> {d.is_critical ? "Critical" : "Mark Critical"}</Btn>}
            {can("devices:modify") && <select data-testid="device-owner" value={d.assigned_contact_id || ""} onChange={(e) => assignPerson(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1.5 text-[13px] text-slate-200 outline-none"><option value="">Unassigned owner</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            {can("devices:modify") && <Btn variant="ghost" onClick={toggleMaint} data-testid="maintenance-btn">{d.status === "MAINTENANCE" ? "Exit Maintenance" : "Maintenance"}</Btn>}
          </div>
        </div>
      </div>

      {d.pending_reboot && can("power:restart") && (
        <div data-testid="reboot-banner" className="flex flex-wrap items-center gap-3 mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-800/60 text-amber-200 text-[13px]">
          <RefreshCw size={16} className="shrink-0" />
          <span className="flex-1">This device is <b>pending a reboot</b> to finish applying updates.</span>
          <button data-testid="reboot-now-btn" onClick={rebootNow} className="font-semibold text-amber-50 bg-amber-600/30 border border-amber-700 rounded-md px-3 py-1">Reboot now</button>
          <button data-testid="reboot-schedule-btn" onClick={() => setRebootOpen(true)} className="font-semibold text-amber-100 underline underline-offset-2">Schedule…</button>
        </div>
      )}
      {rebootOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setRebootOpen(false)}>
          <div className="card-surface rounded-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-slate-100 mb-3">Schedule Reboot</h2>
            <label className="text-[12px] text-slate-400">When</label>
            <input data-testid="reboot-when" type="datetime-local" value={rebootWhen} onChange={(e) => setRebootWhen(e.target.value)} className="mt-1 mb-3 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 outline-none" />
            <label className="text-[12px] text-slate-400">Reason (optional)</label>
            <input data-testid="reboot-reason" value={rebootReason} onChange={(e) => setRebootReason(e.target.value)} className="mt-1 mb-4 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 outline-none" placeholder="Finish patch install" />
            <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setRebootOpen(false)}>Cancel</Btn><Btn data-testid="reboot-schedule-save" onClick={scheduleReboot} disabled={!rebootWhen}>Schedule</Btn></div>
          </div>
        </div>
      )}
      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-800 mb-4 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setTab(t)}
            className={"px-3 py-2 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors " +
              (tab === t ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}>{t}</button>
        ))}
      </div>

      {actionJob && (
        <div data-testid="action-job-status" className={"mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] border " + (actionJob.status === "SUCCEEDED" ? "bg-emerald-950/40 border-emerald-800 text-emerald-300" : ["FAILED", "TIMED_OUT"].includes(actionJob.status) ? "bg-rose-950/40 border-rose-800 text-rose-300" : "bg-slate-800/60 border-slate-700 text-slate-300")}>
          <Activity size={13} className="shrink-0" />
          <span className="font-medium">{actionJob.label}</span>
          <span className="font-mono uppercase">· {actionJob.status}</span>
          {!["SUCCEEDED", "FAILED", "TIMED_OUT", "AWAITING AGENT"].includes(actionJob.status) && <RefreshCw size={12} className="animate-spin" />}
          <button onClick={() => setActionJob(null)} className="ml-auto text-slate-500 hover:text-slate-200"><X size={14} /></button>
        </div>
      )}

      {tab === "Overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Gauge icon={Cpu} label="CPU" value={d.cpu} unit="%" />
          <Gauge icon={MemoryStick} label="Memory" value={d.mem} unit="%" />
          <Gauge icon={HardDrive} label="Disk Used" value={d.disk} unit="%" />
          <Info icon={Clock} label="Uptime" value={fmtUptime(d.uptime_seconds)} />
          <Info icon={Network} label="Local IP" value={d.local_ip || "—"} mono />
          <Info icon={Network} label="Public IP" value={d.public_ip || "—"} mono />
          <Info icon={Cpu} label="CPU" value={hw.cpu?.model || "—"} />
          <Info icon={MemoryStick} label="RAM" value={hw.ram_gb ? hw.ram_gb + " GB" : "—"} />
          <Info icon={Server} label="OS" value={d.os || "—"} />
          <Info icon={RefreshCw} label="Pending Reboot" value={d.pending_reboot ? "Yes" : "No"} tone={d.pending_reboot ? "amber" : "slate"} />
          <Info icon={ShieldCheck} label="Defender" value={sec.defender || "—"} tone="emerald" />
          <Info icon={ShieldCheck} label="BitLocker" value={sec.bitlocker || "—"} tone="emerald" />
          <Info icon={ShieldCheck} label="Firewall" value={sec.firewall || "—"} tone="emerald" />
          <Info icon={Activity} label="Open Alerts" value={d.open_alerts} tone={d.open_alerts ? "rose" : "slate"} />
          <Info label="Device UUID" value={(d.device_uuid || "").slice(0, 16) + "…"} mono />
          <Info label="Serial" value={d.serial_number || "—"} mono />
        </div>
      )}

      {tab === "Hardware" && (
        <div className="grid md:grid-cols-2 gap-3">
          <KV title="System" data={{ Manufacturer: hw.manufacturer, Model: hw.model, Serial: hw.serial_number, RAM: hw.ram_gb ? hw.ram_gb + " GB" : null }} />
          <KV title="Processor" data={{ Model: hw.cpu?.model, Cores: hw.cpu?.cores, Threads: hw.cpu?.threads }} />
          <KV title="BIOS" data={{ Vendor: hw.bios?.vendor, Version: hw.bios?.version, Date: hw.bios?.date }} />
          <KV title="Security Hardware" data={{ TPM: hw.tpm?.present ? `Present v${hw.tpm.version}` : "No", GPU: hw.gpu }} />
          {(hw.storage || []).map((s, i) => <KV key={i} title={`Disk ${i + 1}`} data={{ Model: s.model, Capacity: s.capacity_gb + " GB", Type: s.type, SMART: s.smart }} />)}
        </div>
      )}

      {tab === "Software" && <SimpleTable rows={extra.software || []} cols={["name", "version", "publisher", "architecture"]} empty="No software inventory yet"
        actionLabel="" action={can("software:uninstall") ? (r) => <button data-testid="uninstall-app-btn" onClick={() => uninstallApp(r.name)} className="text-[11px] text-rose-300 bg-rose-950/50 hover:bg-rose-900/50 rounded px-2 py-0.5">Uninstall</button> : null} />}
      {tab === "Processes" && <SimpleTable rows={extra.processes || []} cols={["name", "pid", "user", "cpu", "ram_mb", "path"]} empty="No process data"
        actionLabel="" action={can("processes:terminate") ? (r) => <button data-testid="end-task-btn" onClick={() => endTask(r.pid, r.name)} className="text-[11px] text-rose-300 bg-rose-950/50 hover:bg-rose-900/50 rounded px-2 py-0.5">End task</button> : null} />}
      {tab === "Services" && <SimpleTable rows={extra.services || []} cols={["name", "display_name", "status", "startup_type", "account"]} empty="No service data" />}

      {tab === "Patches" && <PatchManager deviceId={id} hostname={d.hostname} canInstall={can("patch:install")} />}

      {tab === "Metrics" && (
        <div className="grid md:grid-cols-3 gap-3">
          {["cpu", "mem", "disk"].map((k) => (
            <div key={k} className="card-surface rounded-lg p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{k}</div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={extra.metrics || []}>
                  <YAxis hide domain={[0, 100]} /><Tooltip contentStyle={{ background: "#121824", border: "1px solid #1E293B", fontSize: 11 }} />
                  <Line type="monotone" dataKey={k} stroke="#00F0FF" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}

      {tab === "Scripts/Jobs" && (
        <div className="card-surface rounded-lg overflow-hidden">
          {(extra.jobs || []).length === 0 ? <div className="p-8 text-center text-slate-500">No jobs run on this device.</div> : (
            <table className="w-full text-left text-[13px]">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">
                <th className="px-3 py-2">Job</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">By</th><th className="px-3 py-2">When</th></tr></thead>
              <tbody className="divide-y divide-slate-800/60">
                {extra.jobs.map((j) => <JobRow key={j.id} job={j} />)}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "Security" && (
        <div className="grid md:grid-cols-2 gap-3">
          <KV title="Endpoint Security Posture" data={{
            Defender: sec.defender, Firewall: sec.firewall, BitLocker: sec.bitlocker,
            "Secure Boot": sec.secure_boot ? "Enabled" : "Disabled", TPM: sec.tpm ? "Present" : "Absent",
            "Windows Update": sec.windows_update,
          }} />
          <div className="card-surface rounded-lg p-4">
            <h3 className="font-heading font-bold text-[13px] text-slate-200 mb-2 flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-400" /> BitLocker Recovery Key</h3>
            {sec.bitlocker && sec.bitlocker !== "Disabled" && sec.bitlocker !== "Off" ? (
              <div className="space-y-2">
                <code data-testid="bitlocker-key" className="block font-mono text-[12px] text-emerald-300 break-all bg-slate-950/60 rounded px-2.5 py-2">{showKey ? (sec.bitlocker_key || "—") : "••••••-••••••-••••••-••••••-••••••-••••••-••••••-••••••"}</code>
                <div className="flex gap-2">
                  <button data-testid="reveal-bitlocker" onClick={() => setShowKey((v) => !v)} className="inline-flex items-center gap-1 text-[12px] text-cyan-300 bg-cyan-950/40 hover:bg-cyan-900/50 rounded px-2.5 py-1">{showKey ? <EyeOff size={13} /> : <Eye size={13} />}{showKey ? "Hide" : "Reveal"}</button>
                  <button data-testid="copy-bitlocker" onClick={() => { navigator.clipboard.writeText(sec.bitlocker_key || ""); toast.success("Recovery key copied"); }} className="inline-flex items-center gap-1 text-[12px] text-slate-300 bg-slate-800 hover:bg-slate-700 rounded px-2.5 py-1"><Copy size={13} /> Copy</button>
                </div>
                <p className="text-[11px] text-slate-500">Store this key securely — it unlocks the drive if the device can't boot normally.</p>
              </div>
            ) : <div className="text-[13px] text-slate-500">Drive is not encrypted — no recovery key available.</div>}
          </div>
        </div>
      )}

      {tab === "Audit History" && (
        <div className="card-surface rounded-lg overflow-hidden">
          {(extra.audit || []).length === 0 ? <div className="p-8 text-center text-slate-500">No audit events.</div> : (
            <table className="w-full text-left text-[13px]">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">
                <th className="px-3 py-2">Action</th><th className="px-3 py-2">Actor</th><th className="px-3 py-2">Result</th><th className="px-3 py-2">When</th></tr></thead>
              <tbody className="divide-y divide-slate-800/60">
                {extra.audit.map((a) => <tr key={a.id} className="hover-row"><td className="px-3 py-1.5 font-mono text-cyan-300">{a.action}</td><td className="px-3 py-1.5 text-slate-400">{a.actor}</td><td className="px-3 py-1.5 text-slate-400">{a.result}</td><td className="px-3 py-1.5 text-slate-500">{timeAgo(a.timestamp)}</td></tr>)}
              </tbody>
            </table>
          )}
        </div>
      )}

      {termOpen && <Terminal_ device={d} onClose={() => setTermOpen(false)} />}
      {remotePick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setRemotePick(false)}>
          <div className="card-surface rounded-xl p-5 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()} data-testid="remote-pick-modal">
            <h2 className="font-heading font-bold text-slate-100 mb-3">Start remote session</h2>
            <div className="space-y-2">
              {providers.map((p) => <button key={p.id} data-testid="remote-provider-option" onClick={() => startRemote(p.id)} className="w-full flex items-center gap-2 text-left bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-[13px] text-slate-200"><ExternalLink size={14} className="text-cyan-400" /> {p.name}</button>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Gauge({ icon: Icon, label, value, unit }) {
  const v = value || 0;
  const tone = v > 90 ? "text-rose-400" : v > 75 ? "text-amber-400" : "text-cyan-300";
  return (
    <div className="card-surface rounded-lg p-4">
      <div className="flex items-center justify-between mb-1"><span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span><Icon size={15} className="text-slate-500" /></div>
      <div className={"font-heading font-extrabold text-3xl " + tone}>{v}<span className="text-lg">{unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full mt-2 overflow-hidden"><div className={"h-full rounded-full " + (v > 90 ? "bg-rose-500" : v > 75 ? "bg-amber-500" : "bg-cyan-500")} style={{ width: v + "%" }} /></div>
    </div>
  );
}
function Info({ icon: Icon, label, value, tone = "slate", mono }) {
  const tones = { slate: "text-slate-200", rose: "text-rose-300", amber: "text-amber-300", emerald: "text-emerald-300" };
  return <div className="card-surface rounded-lg p-4"><div className="flex items-center justify-between mb-1"><span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</span>{Icon && <Icon size={15} className="text-slate-500" />}</div><div className={"font-semibold text-lg " + tones[tone] + (mono ? " font-mono text-sm" : "")}>{value}</div></div>;
}
function KV({ title, data }) {
  return <div className="card-surface rounded-lg p-4"><h3 className="font-heading font-bold text-[13px] text-slate-200 mb-2">{title}</h3><div className="space-y-1.5">{Object.entries(data).filter(([, v]) => v != null).map(([k, v]) => <div key={k} className="flex justify-between text-[13px]"><span className="text-slate-500">{k}</span><span className="text-slate-200 font-mono">{String(v)}</span></div>)}</div></div>;
}
function SimpleTable({ rows, cols, empty, action, actionLabel }) {
  if (!rows.length) return <div className="card-surface rounded-lg p-8 text-center text-slate-500">{empty}</div>;
  return <div className="card-surface rounded-lg overflow-hidden overflow-x-auto"><table className="w-full text-left text-[13px]"><thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">{cols.map((c) => <th key={c} className="px-3 py-2">{c.replace(/_/g, " ")}</th>)}{action && <th className="px-3 py-2 text-right">{actionLabel}</th>}</tr></thead><tbody className="divide-y divide-slate-800/60">{rows.map((r, i) => <tr key={i} className="hover-row">{cols.map((c) => <td key={c} className="px-3 py-1.5 text-slate-300 font-mono truncate max-w-xs">{String(r[c] ?? "—")}</td>)}{action && <td className="px-3 py-1.5 text-right">{action(r)}</td>}</tr>)}</tbody></table></div>;
}
function JobRow({ job }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const load = () => { setOpen(!open); if (!full) api.get("/jobs/" + job.id).then((r) => setFull(r.data)); };
  const sc = { QUEUED: "text-slate-400", COMPLETED: "text-emerald-400", FAILED: "text-rose-400" };
  return (
    <>
      <tr className="hover-row cursor-pointer" onClick={load}><td className="px-3 py-1.5 text-slate-200">{job.title}</td><td className="px-3 py-1.5 text-slate-400 font-mono">{job.command_type}</td><td className={"px-3 py-1.5 font-mono " + (sc[job.status] || "text-slate-400")}>{job.status}</td><td className="px-3 py-1.5 text-slate-400">{job.requested_by}</td><td className="px-3 py-1.5 text-slate-500">{timeAgo(job.created_at)}</td></tr>
      {open && full && full.targets?.map((t) => <tr key={t.id}><td colSpan={5} className="px-6 py-2 bg-slate-950/60"><div className="text-[11px] text-slate-500 mb-1">exit {t.exit_code ?? "—"} · {t.status}</div><pre className="text-[11px] text-emerald-300 whitespace-pre-wrap font-mono max-h-40 overflow-y-auto">{t.stdout || t.stderr || "(no output)"}</pre></td></tr>)}
    </>
  );
}
function Terminal_({ device, onClose }) {
  const [lang, setLang] = useState("powershell");
  const [cmd, setCmd] = useState("");
  const [lines, setLines] = useState([{ t: "sys", v: `Connected to ${device.hostname} · ${device.status}. Commands run via signed job on the agent.` }]);
  const run = async () => {
    if (!cmd.trim()) return;
    setLines((l) => [...l, { t: "in", v: `${lang}> ${cmd}` }]);
    const c = cmd; setCmd("");
    try {
      const { data } = await api.post("/jobs/run-command", { device_id: device.id, language: lang, command: c });
      setLines((l) => [...l, { t: "sys", v: "Job dispatched, awaiting agent…" }]);
      const jid = data.id;
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        const r = await api.get("/jobs/" + jid);
        const tg = r.data.targets?.[0];
        if (tg && ["SUCCEEDED", "FAILED", "TIMED_OUT"].includes(tg.status)) {
          clearInterval(poll);
          setLines((l) => [...l, { t: "out", v: tg.stdout || tg.stderr || "(no output)" }]);
        } else if (tries > 20) { clearInterval(poll); setLines((l) => [...l, { t: "sys", v: "Timed out waiting for device (is the agent online?)" }]); }
      }, 2000);
    } catch (e) { setLines((l) => [...l, { t: "sys", v: "Error: " + apiErr(e) }]); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="w-full max-w-2xl mx-4 card-surface rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800">
          <Terminal size={15} className="text-cyan-400" />
          <span className="font-heading font-bold text-[13px] text-slate-200">Remote Terminal · {device.hostname}</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="ml-auto bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[12px] text-slate-200"><option value="powershell">PowerShell</option><option value="cmd">CMD</option></select>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 ml-2">✕</button>
        </div>
        <div className="bg-slate-950 h-80 overflow-y-auto p-3 font-mono text-[12px] space-y-1">
          {lines.map((l, i) => <div key={i} className={l.t === "in" ? "text-cyan-300" : l.t === "sys" ? "text-slate-500 italic" : "text-emerald-300 whitespace-pre-wrap"}>{l.v}</div>)}
        </div>
        <div className="flex items-center gap-2 px-3 py-2 border-t border-slate-800">
          <span className="text-cyan-400 font-mono text-sm">$</span>
          <input data-testid="terminal-input" value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} autoFocus placeholder="Type a command… (try: hostname)"
            className="flex-1 bg-transparent outline-none text-sm text-slate-100 font-mono" />
          <Btn onClick={run}><Play size={13} /> Run</Btn>
        </div>
      </div>
    </div>
  );
}
